import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../shared/ui/button.js";

export interface TastytradeCredentialInput {
  readonly clientId?: string;
  readonly clientSecret: string;
  readonly refreshToken: string;
}

interface TastytradeConnectionFormProps {
  readonly busy?: boolean;
  readonly onConnect: (credentials: TastytradeCredentialInput) => Promise<void>;
  readonly reconnect?: boolean;
}

export function TastytradeConnectionForm({
  busy = false,
  onConnect,
  reconnect = false,
}: TastytradeConnectionFormProps) {
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, reset } = useForm<TastytradeCredentialInput>();
  const submit = handleSubmit(async (values) => {
    setError(null);
    try {
      await onConnect({
        ...(values.clientId === undefined || values.clientId.trim() === ""
          ? {}
          : { clientId: values.clientId.trim() }),
        clientSecret: values.clientSecret,
        refreshToken: values.refreshToken,
      });
      reset({ clientId: "", clientSecret: "", refreshToken: "" });
    } catch {
      reset({ clientId: "", clientSecret: "", refreshToken: "" });
      setError("Tastytrade could not be connected. Verify the OAuth credentials and try again.");
    }
  });
  return (
    <form className="form-stack" onSubmit={(event) => void submit(event)}>
      <div className="field-stack">
        <label htmlFor="tastytrade-client-id">
          OAuth client ID <span>(optional)</span>
        </label>
        <input id="tastytrade-client-id" autoComplete="off" {...register("clientId")} />
      </div>
      <div className="field-stack">
        <label htmlFor="tastytrade-client-secret">OAuth client secret</label>
        <input
          id="tastytrade-client-secret"
          type="password"
          autoComplete="new-password"
          required
          {...register("clientSecret", { required: true })}
        />
      </div>
      <div className="field-stack">
        <label htmlFor="tastytrade-refresh-token">Refresh token</label>
        <input
          id="tastytrade-refresh-token"
          type="password"
          autoComplete="new-password"
          required
          {...register("refreshToken", { required: true })}
        />
      </div>
      <p className="field-hint">
        Credentials are sent once to TradingEngine Secrets Manager and are never returned to this
        browser.
      </p>
      {error === null ? null : <p className="form-message form-message--error">{error}</p>}
      <Button disabled={busy} type="submit">
        {busy ? "Connecting…" : reconnect ? "Reconnect Tastytrade" : "Connect Tastytrade"}
      </Button>
    </form>
  );
}
